/*
  BizTracker System Builder engine. Pure functions, no DOM, no network, no storage.

  ONE COPY OF THE RULES. PR #59 kept its recommendation rules twice, once in a Node
  module and once in the browser controller, and held the two together with a test.
  This file is the only copy: the storefront loads it as a theme asset, and the Node
  tests and the compiler load the very same file (scripts/system-builder/engine.mjs),
  so what is tested is what ships.

  WHAT IT MAY CLAIM. The compiled payload (theme/assets/biztracker-system-builder-
  data.json, built by scripts/system-builder/build-theme-data.mjs from the canonical
  Builder files) says, per tier, whether the current Builder gates find it offerable,
  and names models only for a tier they do. This engine never names a model the
  payload did not name, never promotes a slot to verified, never infers a substitute,
  and never turns a "not needed" answer into a missing slot. With no tier offerable,
  which is the state of the data today, every slot reads "Verification required".

  WHAT IT MAY NOT HOLD. Contact identity. State is the shopper's answers about the
  store and nothing else; the lead payload and every analytics detail are built from
  explicit allow-lists, so a field added to state does not leak by default.
*/
(function (root) {
  'use strict';

  var VERSION = '2026-09-18-v1';
  var SOURCE_PATH = '/pages/build-a-pos-system';
  var VERIFICATION_REQUIRED = 'verification_required';
  var VERIFIED = 'verified';
  var MAX_STATIONS = 99;
  var REVIEW_INTEREST = { review_with_system: true, not_sure: true };
  // The lead handoff contract (data/system-builder/lead-handoff-contract.json). A number,
  // bumped with the contract, so the CRM lane can tell one shape from the next.
  var LEAD_CONTRACT_VERSION = 2;
  var PURCHASE_DIRECT = 'direct';
  var PURCHASE_QUOTE = 'quote';
  var PRICE_SHOWN = 'shown';
  var PRICE_HIDDEN = 'hidden';
  // The Builder shows no price for any line: no approved commercial fact covers the Build
  // page, so every line of every package is quoted by a person.
  var PRICING_BASIS = 'quoted';

  function has(list, id) {
    for (var i = 0; i < list.length; i += 1) if (list[i].id === id) return true;
    return false;
  }

  function find(list, id) {
    for (var i = 0; i < list.length; i += 1) if (list[i].id === id) return list[i];
    return null;
  }

  function own(table, key) {
    return typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key) && table[key] === true;
  }

  // THE STOREFRONT COMMERCE GATE, RESTATED FOR THE BUILDER. snippets/biztracker-commerce-gate.liquid
  // decides every storefront price and buy control from the generated resolver
  // (snippets/biztracker-cta-resolve.liquid); the Builder decides its lines by the same rule:
  //   resolved   the purchase mode is one the resolver knows; a missing or unknown mode
  //              resolves to nothing, so nothing prices and nothing carts (fail closed);
  //   price      shown only if resolved AND the visibility shows a price (public, starting_at)
  //              AND the variant is priced above zero; the purchase mode does not decide it,
  //              so quote_only with public visibility shows its price and never carts;
  //   buyable    only a shown price whose purchase mode carts (direct).
  // The Builder then asks more than the gate, never less: the SKU must be ACTIVE, published
  // and price-verified, or the line is a quote with no price. It can only ever be stricter.
  // tests/system-builder-r2.test.mjs runs every combination against the Liquid itself.
  var RESOLVED_MODES = { direct: true, review_required: true, quote_only: true, not_buyable: true };
  var CARTING_MODES = { direct: true };
  var PRICED_VISIBILITIES = { public: true, starting_at: true };

  function gateDecision(record) {
    var r = record && typeof record === 'object' ? record : {};
    var resolved = own(RESOLVED_MODES, r.purchase_mode);
    var showPrice = resolved && own(PRICED_VISIBILITIES, r.price_visibility) && r.priced_above_zero === true;
    return { resolved: resolved, showPrice: showPrice, buyable: showPrice && own(CARTING_MODES, r.purchase_mode) };
  }

  function commerceDecision(record) {
    var r = record && typeof record === 'object' ? record : {};
    var gate = gateDecision(r);
    var live = r.status === 'ACTIVE' && r.published === true && r.price_verification_status === 'verified';
    var showPrice = gate.showPrice && live;
    var buyable = gate.buyable && live;
    return {
      resolved: gate.resolved,
      showPrice: showPrice,
      buyable: buyable,
      purchase: buyable ? PURCHASE_DIRECT : PURCHASE_QUOTE,
      priceDisplay: showPrice ? PRICE_SHOWN : PRICE_HIDDEN
    };
  }

  function flags(list, source) {
    var out = {};
    for (var i = 0; i < list.length; i += 1) {
      out[list[i].id] = Boolean(source) && source[list[i].id] === true;
    }
    return out;
  }

  // A closed shape. Anything the caller sends that the flow does not define is
  // dropped, and anything missing takes its safe default, so stored or hinted state
  // can never carry a question that no longer exists, or an identity, into a scope.
  function normalizeState(input, payload) {
    var flow = payload.flow;
    var source = input && typeof input === 'object' ? input : {};
    var businessType = has(flow.businessTypes, source.businessType) ? source.businessType : null;

    var requested = Number(source.checkoutStations);
    var checkoutStations = isFinite(requested) && Math.floor(requested) === requested && requested >= 1
      ? Math.min(requested, MAX_STATIONS)
      : 1;

    var delivery = has(flow.services.delivery, source.delivery) ? source.delivery : flow.services.delivery[0].id;
    var additions = [];
    if (Array.isArray(source.additions)) {
      for (var i = 0; i < source.additions.length; i += 1) {
        var id = source.additions[i];
        if (has(flow.services.additions, id) && additions.indexOf(id) === -1) additions.push(id);
      }
    }

    var processing = has(flow.processing.choices, source.processing) ? source.processing : 'not_sure';
    var tier = has(payload.ladder.tiers, source.tier) ? source.tier : payload.ladder.recommendedTier;

    return {
      businessType: businessType,
      storeNeeds: flags(flow.storeNeeds, source.storeNeeds),
      checkoutStations: checkoutStations,
      counterNeeds: flags(flow.counterNeeds, source.counterNeeds),
      delivery: delivery,
      additions: additions,
      processing: processing,
      tier: tier
    };
  }

  // THE HINT AN INDUSTRY PAGE MAY PASS: "#business=grocery". It preselects a retail
  // business type and does nothing else (D8). A restaurant, an unknown word, or any
  // other shape is ignored rather than guessed at.
  function presetFromHash(hash, payload) {
    var match = /^#business=([a-z_]+)$/.exec(String(hash || ''));
    if (!match) return null;
    return payload.presets.indexOf(match[1]) === -1 ? null : match[1];
  }

  function counterAnswerFor(flow, slot, counterNeeds) {
    for (var i = 0; i < flow.counterNeeds.length; i += 1) {
      if (flow.counterNeeds[i].slot === slot) return counterNeeds[flow.counterNeeds[i].id] === true;
    }
    return null;   // no question concerns this slot: the station itself is always needed
  }

  // THE SOFTWARE PLAN SLOT. Plans are neutral placeholder slots (data/system-builder/
  // software-plan-slots.json) until Kris names them: the payload carries a slot only when it
  // is mapped to a package tier, and a label only when one is supplied AND approved word for
  // word. With no label the slot is internal: its id goes to sales, and the shopper sees
  // nothing. The Builder never carries a plan price.
  function planSlotFor(payload, tierId) {
    var slots = payload.software && Array.isArray(payload.software.planSlots) ? payload.software.planSlots : [];
    for (var i = 0; i < slots.length; i += 1) {
      var tiers = Array.isArray(slots[i].packageTiers) ? slots[i].packageTiers : [];
      if (tiers.indexOf(tierId) !== -1) {
        var label = typeof slots[i].label === 'string' && slots[i].label ? slots[i].label : null;
        return { id: slots[i].id, label: label };
      }
    }
    return null;
  }

  function derive(state, payload) {
    var flow = payload.flow;
    var type = find(flow.businessTypes, state.businessType);
    var path = type ? type.path : null;
    var notes = [];
    var noteIds = [];
    function note(id) {
      if (noteIds.indexOf(id) !== -1) return;
      noteIds.push(id);
      notes.push({ id: id, text: flow.reviewNotes[id] });
    }

    var starting = null;
    var additional = [];
    var softwarePlan = null;

    if (path === 'retail') {
      var tier = find(payload.ladder.tiers, state.tier) || find(payload.ladder.tiers, payload.ladder.recommendedTier);
      var slots = [];
      for (var i = 0; i < tier.slots.length; i += 1) {
        var s = tier.slots[i];
        var basis = flow.slotQuantities[s.slot];
        var needed = counterAnswerFor(flow, s.slot, state.counterNeeds);
        // A slot is verified only where the compiled payload says the tier is offerable
        // AND carries the model. Nothing here can make it so.
        var verified = tier.offerable === true && s.state === VERIFIED && Boolean(s.model);
        // HOW IT IS BOUGHT. A slot is bought directly only where the compiled payload says
        // so for a verified slot, which it does only for a product the live catalog
        // governance records as active, direct-purchase and publicly priced. Everything
        // else is quoted, so the Builder never implies a price it cannot show.
        // A price is shown only where the payload says so for a verified slot, and a slot is
        // bought directly only where its price is also shown: the gate never carts a line
        // whose price it hides, and neither does the Builder.
        var priceDisplay = verified && s.price_display === PRICE_SHOWN ? PRICE_SHOWN : PRICE_HIDDEN;
        var purchase = priceDisplay === PRICE_SHOWN && s.purchase === PURCHASE_DIRECT ? PURCHASE_DIRECT : PURCHASE_QUOTE;
        slots.push({
          slot: s.slot,
          label: s.label,
          quantityBasis: basis,
          quantity: basis === 'per_station' ? state.checkoutStations : null,
          neededAtCounter: needed,
          state: verified ? VERIFIED : VERIFICATION_REQUIRED,
          model: verified ? s.model : null,
          purchase: purchase,
          priceDisplay: priceDisplay,
          review: needed === false ? flow.reviewNotes.slot_not_needed : null
        });
      }
      starting = {
        tier: { id: tier.id, name: tier.name, recommended: tier.recommended === true, offerable: tier.offerable === true },
        slots: slots
      };
      softwarePlan = planSlotFor(payload, tier.id);

      var scopeIds = [];
      var needs = flow.storeNeeds.concat(flow.counterNeeds);
      for (var j = 0; j < needs.length; j += 1) {
        var answer = needs[j];
        var chosen = state.storeNeeds[answer.id] === true || state.counterNeeds[answer.id] === true;
        if (chosen && answer.scope && scopeIds.indexOf(answer.scope) === -1) scopeIds.push(answer.scope);
        if (chosen && answer.review) note(answer.review);
      }
      for (var k = 0; k < flow.additionalScope.length; k += 1) {
        if (scopeIds.indexOf(flow.additionalScope[k].id) !== -1) {
          additional.push({ id: flow.additionalScope[k].id, label: flow.additionalScope[k].label });
        }
      }
    } else if (path === 'restaurant') {
      note('linga_scope_review');
    }

    if (path !== 'restaurant') {
      if (state.additions.indexOf('existing_equipment_check') !== -1) note('existing_equipment_review');
      if (state.additions.indexOf('data_conversion') !== -1) note('item_file_review');
    }

    var verificationRequired = 0;
    if (starting) {
      for (var m = 0; m < starting.slots.length; m += 1) {
        if (starting.slots[m].state === VERIFICATION_REQUIRED) verificationRequired += 1;
      }
      if (verificationRequired > 0) note('verification_required');
    }

    // A restaurant is scoped with a person on the LINGA path and is never asked the
    // retail questions, so its scope carries no implementation or processing answer:
    // reporting the defaults would state choices the shopper never made. The two
    // processing paths are for Infinity retail systems, so none is named for it either.
    var services = null;
    var processing = null;
    if (path !== 'restaurant') {
      var additions = [];
      for (var n = 0; n < state.additions.length; n += 1) {
        var addition = find(flow.services.additions, state.additions[n]);
        additions.push({ id: addition.id, label: addition.label });
      }
      var delivery = find(flow.services.delivery, state.delivery);
      services = { delivery: { id: delivery.id, label: delivery.label }, additions: additions };
      var interest = find(flow.processing.choices, state.processing);
      processing = {
        interest: { id: interest.id, label: interest.label },
        paths: path === 'retail' && REVIEW_INTEREST[state.processing] ? flow.processing.paths.slice() : []
      };
    }

    return {
      businessType: type ? { id: type.id, label: type.label } : null,
      path: path,
      platform: path ? payload.platforms[path] : null,
      checkoutStations: state.checkoutStations,
      startingConfiguration: starting,
      softwarePlan: softwarePlan,
      additionalScope: additional,
      services: services,
      processing: processing,
      reviewNotes: notes,
      verificationRequiredCount: verificationRequired,
      restaurantRoute: path === 'restaurant' ? flow.restaurantRoute : null
    };
  }

  // WHERE THE LEAD CAME FROM. Read from what the page already holds (window.btAttribution,
  // which biztracker-attribution.liquid sets only with analytics AND marketing consent, and
  // with no ad click id without sale-of-data consent) and from a same-origin referrer path,
  // never from the URL directly. Each value is allow-listed by shape: a path is a same-site
  // path with no query (never protocol-relative), a campaign value is short plain text with no at-sign, so an email typed into a
  // campaign link is dropped rather than sent.
  var ATTRIBUTION_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'landing_page'];
  function safePath(value) {
    return typeof value === 'string' && value.length <= 200 && /^\/(?!\/)[A-Za-z0-9\-._~%\/]*$/.test(value) ? value : null;
  }
  function safeCampaignValue(key, value) {
    if (typeof value !== 'string' || value.length === 0 || value.length > 150) return null;
    if (key === 'gclid') return /^[A-Za-z0-9_\-]+$/.test(value) ? value : null;
    if (key === 'landing_page') return safePath(value);
    return /^[A-Za-z0-9 _.+\-|:]+$/.test(value) ? value : null;
  }
  function safeAttribution(source) {
    if (!source || typeof source !== 'object') return null;
    var out = {};
    var any = false;
    for (var i = 0; i < ATTRIBUTION_FIELDS.length; i += 1) {
      var key = ATTRIBUTION_FIELDS[i];
      var value = Object.prototype.hasOwnProperty.call(source, key) ? safeCampaignValue(key, source[key]) : null;
      out[key] = value;
      if (value !== null) any = true;
    }
    return any ? out : null;
  }

  // FOR SALES, AND LATER FOR A CRM ADAPTER. Built field by field from the scope, never
  // by copying state, so it cannot carry anything the scope does not: no name, no email,
  // no phone, no business name, no message. Identity travels in the contact form's own
  // fields at submission time and nowhere else.
  function leadPayload(result, state, meta) {
    var starting = result.startingConfiguration;
    var slots = [];
    if (starting) {
      for (var i = 0; i < starting.slots.length; i += 1) {
        var s = starting.slots[i];
        slots.push({
          slot: s.slot,
          quantity_basis: s.quantityBasis,
          quantity: s.quantity,
          needed_at_counter: s.neededAtCounter,
          state: s.state,
          purchase: s.purchase,
          price_display: s.priceDisplay
        });
      }
    }
    var notes = [];
    for (var j = 0; j < result.reviewNotes.length; j += 1) notes.push(result.reviewNotes[j].id);
    var scope = [];
    for (var k = 0; k < result.additionalScope.length; k += 1) scope.push(result.additionalScope[k].id);
    var additions = [];
    if (result.services) {
      for (var m = 0; m < result.services.additions.length; m += 1) additions.push(result.services.additions[m].id);
    }
    // The retail answers are reported only where the retail questions were asked.
    var retail = result.path === 'retail';
    // THE NAMED PACKAGE. Built only from names the payload already carries (the platform
    // and the tier's approved display name) and from ids, so it adds no wording of its
    // own. A restaurant has no package: it is scoped on its own path with a person.
    var packageKey = null;
    var packageName = null;
    if (retail && starting) {
      packageKey = String(result.platform).toLowerCase() + '_' + result.path + '_' + starting.tier.id;
      packageName = result.platform + ' ' + starting.tier.name;
    } else if (result.path === 'restaurant') {
      packageKey = String(result.platform).toLowerCase() + '_' + result.path;
    }

    return {
      builder_payload_version: VERSION,
      lead_contract_version: LEAD_CONTRACT_VERSION,
      source_path: SOURCE_PATH,
      originating_page: meta ? safePath(meta.originatingPage) : null,
      attribution: meta ? safeAttribution(meta.attribution) : null,
      package_key: packageKey,
      package_name: packageName,
      vertical: result.businessType ? result.businessType.id : null,
      terminal_count: retail ? result.checkoutStations : null,
      pricing_basis: PRICING_BASIS,
      preset: meta && meta.preset ? meta.preset : null,
      business_type: result.businessType ? result.businessType.id : null,
      path: result.path,
      platform: result.platform,
      checkout_stations: retail ? result.checkoutStations : null,
      store_needs: retail ? copyFlags(state.storeNeeds) : null,
      counter_needs: retail ? copyFlags(state.counterNeeds) : null,
      starting_configuration: starting
        ? { tier: starting.tier.id, recommended: starting.tier.recommended, offerable: starting.tier.offerable, slots: slots }
        : null,
      software_plan_slot: retail && result.softwarePlan ? result.softwarePlan.id : null,
      additional_scope: scope,
      implementation: result.services ? { delivery: result.services.delivery.id, additions: additions } : null,
      processing_interest: result.processing ? result.processing.interest.id : null,
      processing_paths_for_review: result.processing ? result.processing.paths.slice() : [],
      review_notes: notes,
      verification_required_count: result.verificationRequiredCount
    };
  }

  function copyFlags(source) {
    var out = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) out[key] = source[key] === true;
    }
    return out;
  }

  function summaryText(result, payload) {
    var lines = [];
    var platform = result.platform || 'to be decided';
    lines.push((result.businessType ? result.businessType.label : 'Business type not chosen') + ' — ' + platform);
    if (result.path !== 'restaurant') lines.push('Checkout stations: ' + result.checkoutStations);
    if (result.startingConfiguration) {
      var starting = result.startingConfiguration;
      lines.push('', 'Starting configuration: ' + starting.tier.name);
      if (result.softwarePlan && result.softwarePlan.label) lines.push(result.softwarePlan.label);
      for (var i = 0; i < starting.slots.length; i += 1) {
        var s = starting.slots[i];
        var qty = s.quantity == null ? 'per store, scoped with sales' : 'x' + s.quantity;
        var what = s.model ? s.model : 'Verification required';
        lines.push('- ' + s.label + ' (' + qty + '): ' + what + (s.review ? ' — ' + s.review : ''));
      }
      if (result.additionalScope.length > 0) {
        lines.push('', 'Additional scope to review:');
        for (var j = 0; j < result.additionalScope.length; j += 1) lines.push('- ' + result.additionalScope[j].label);
      }
    }
    if (result.restaurantRoute) lines.push('', result.restaurantRoute.note);
    if (result.services) {
      lines.push('', 'Implementation: ' + result.services.delivery.label);
      for (var k = 0; k < result.services.additions.length; k += 1) lines.push('- ' + result.services.additions[k].label);
    }
    if (result.processing) {
      lines.push('', 'Card processing: ' + result.processing.interest.label);
      if (result.processing.paths.length > 0) lines.push('- Paths reviewed: ' + result.processing.paths.join(' and '));
    }
    if (result.reviewNotes.length > 0) {
      lines.push('', 'Review notes:');
      for (var m = 0; m < result.reviewNotes.length; m += 1) lines.push('- ' + result.reviewNotes[m].text);
    }
    lines.push('', payload.flow.summaryNote);
    return lines.join('\n');
  }

  // THE ONLY FIELDS AN ANALYTICS EVENT MAY CARRY, AND THE ONLY VALUES. Named one by
  // one, so a new field is a visible edit here rather than a state object quietly
  // widening; and each value must be one the flow itself defines (an id, a count, a
  // flag), so an email or a phone number put in any of them is dropped, not sent.
  var EVENT_FIELDS = [
    'builder_version', 'step_id', 'business_type', 'path', 'checkout_stations',
    'processing_interest', 'tier', 'verification_required_count', 'preset_used'
  ];
  function ids(list) {
    var out = [];
    for (var i = 0; i < list.length; i += 1) out.push(list[i].id);
    return out;
  }
  function wholeNumber(value, min, max) {
    return typeof value === 'number' && Math.floor(value) === value && value >= min && value <= max;
  }
  function safeEventDetail(source, payload) {
    var input = source && typeof source === 'object' ? source : {};
    var flow = payload.flow;
    var allowed = {
      builder_version: function (v) { return v === VERSION; },
      step_id: function (v) { return ids(flow.steps).indexOf(v) !== -1; },
      business_type: function (v) { return ids(flow.businessTypes).indexOf(v) !== -1; },
      path: function (v) { return v === 'retail' || v === 'restaurant'; },
      checkout_stations: function (v) { return wholeNumber(v, 1, MAX_STATIONS); },
      processing_interest: function (v) { return ids(flow.processing.choices).indexOf(v) !== -1; },
      tier: function (v) { return ids(payload.ladder.tiers).indexOf(v) !== -1; },
      verification_required_count: function (v) { return wholeNumber(v, 0, 50); },
      preset_used: function (v) { return v === true || v === false; }
    };
    var detail = {};
    for (var i = 0; i < EVENT_FIELDS.length; i += 1) {
      var key = EVENT_FIELDS[i];
      if (Object.prototype.hasOwnProperty.call(input, key) && allowed[key](input[key])) detail[key] = input[key];
    }
    return detail;
  }

  root.BizTrackerSystemBuilderEngine = {
    VERSION: VERSION,
    SOURCE_PATH: SOURCE_PATH,
    VERIFICATION_REQUIRED: VERIFICATION_REQUIRED,
    VERIFIED: VERIFIED,
    LEAD_CONTRACT_VERSION: LEAD_CONTRACT_VERSION,
    PRICING_BASIS: PRICING_BASIS,
    ATTRIBUTION_FIELDS: ATTRIBUTION_FIELDS.slice(),
    MAX_STATIONS: MAX_STATIONS,
    EVENT_FIELDS: EVENT_FIELDS.slice(),
    normalizeState: normalizeState,
    presetFromHash: presetFromHash,
    derive: derive,
    leadPayload: leadPayload,
    gateDecision: gateDecision,
    commerceDecision: commerceDecision,
    summaryText: summaryText,
    safeEventDetail: safeEventDetail
  };
})(typeof self !== 'undefined' ? self : this);
